import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, AlertIcon, Box, Button, Flex, Grid, IconButton, Input, InputGroup, InputLeftElement, Menu, MenuButton,
  MenuItem, MenuList, Select, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden, useToast,
} from '@chakra-ui/react';
import { FiChevronDown, FiDownload, FiMessageSquare, FiMoreHorizontal, FiPaperclip, FiPlus, FiSearch, FiCreditCard, FiSliders, FiTag } from 'react-icons/fi';
import useAuth from 'hooks/useAuth';
import AiPagination from 'components/monitoring/ai/AiPagination';
import {
  EDIT_ROLES, REVIEW_ROLES, MONTHS, REVIEW_LABEL, getFinanceOptions, getSummary, getMovements, voidMovement, reviewMovement,
  downloadExport, errorMessage, formatAmount, formatDay, formatMoney, monthLabel,
} from 'api/administrador/finance';
import { Card, EmptyState, KpiTile, PageHeader, PeriodStatusTag, SegmentedButtons, Tag, MonthStepper } from './ui';
import { CategoryBars, DailyBalance, PairBars } from './charts';
import MovementDrawer from './MovementDrawer';
import OpeningModal from './OpeningModal';
import CategoriesModal from './CategoriesModal';
import HistoryDrawer from './HistoryDrawer';
import PromptModal from './PromptModal';
import CardPlatformsModal from './CardPlatformsModal';

const PAGE_SIZE = 20;
const FILTERS = { q: '', type: '', category: '', note: '', min: '', max: '', studentCode: '' };

function useDebounced(value, ms = 400) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Recuadro de la caja inicial del mes de la cuenta. */
function OpeningBanner({ account, period, year, month, canEdit, onOpen, onNewMovement }) {
  const isCaja = account.book === 'caja_menor';
  if (period.status === 'falta' || period.opening === null) {
    return (
      <Card>
        <EmptyState
          title={`${account.name} no tiene caja inicial de ${MONTHS[month - 1]}`}
          description={account.currency === 'USD'
            ? 'Configúrala en dólares. Los movimientos se registran en USD y se convierten a pesos con la TRM.'
            : account.kind === 'tarjeta'
              ? 'Para la tarjeta, la caja inicial es lo que se debe al empezar el mes. El pago desde Bancolombia es un traslado.'
              : 'Configúrala para empezar a registrar los movimientos del mes.'}
          action={canEdit && <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} leftIcon={<FiSliders />} onClick={onOpen}>Configurar caja inicial</Button>}
        />
      </Card>
    );
  }
  const hasDiff = period.status === 'diferencia' || period.status === 'explicada';
  return (
    <Box bg="purple.50" border="1px solid" borderColor="purple.100" borderRadius="lg" px={5} py={4}>
      <Flex gap={{ base: 4, md: 10 }} wrap="wrap" align="center">
        <Box minW={0}>
          <Text fontSize="xs" fontWeight="600" color="purple.800" textTransform="uppercase" letterSpacing="0.06em">
            Caja inicial de {MONTHS[month - 1]}{isCaja ? '' : ` · ${account.name}`}
          </Text>
          <Text fontSize="22px" fontWeight="700" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(period.opening, account.currency)}</Text>
          <Text fontSize="xs" color="gray.500">
            {period.status === 'automatica' ? `Se tomó sola del cierre de ${MONTHS[(month + 10) % 12]}` : period.status === 'primer_mes' ? 'Primer mes de esta cuenta' : period.periodSource === 'importado' ? 'Migrada del Excel' : 'Configurada por Finanzas'}
          </Text>
        </Box>
        {period.calc !== null && !isCaja && (
          <Box>
            <Text fontSize="xs" color="gray.500">Cierre calculado de {MONTHS[(month + 10) % 12]}</Text>
            <Text fontWeight="600" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(period.calc, account.currency)}</Text>
          </Box>
        )}
        {(hasDiff || period.status === 'cuadra') && (
          <Box>
            <Text fontSize="xs" color="gray.500">Diferencia</Text>
            <Flex gap={2} align="center">
              {hasDiff && <Text fontWeight="600" color={period.status === 'explicada' ? 'blue.600' : 'orange.600'} sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(period.diff, account.currency)}</Text>}
              <PeriodStatusTag status={period.status} />
            </Flex>
          </Box>
        )}
        {isCaja && (account.minBalance !== null || account.maxBalance !== null) && (
          <Box>
            <Text fontSize="xs" color="gray.500">Topes de la caja</Text>
            <Text fontWeight="600" sx={{ fontVariantNumeric: 'tabular-nums' }}>Mín. {formatMoney(account.minBalance)} · Máx. {formatMoney(account.maxBalance)}</Text>
          </Box>
        )}
      </Flex>
      {period.status === 'diferencia' && (
        <Flex mt={3} pt={3} borderTop="1px solid" borderColor="purple.100" gap={{ base: 2, md: 5 }} wrap="wrap" align="center" fontSize="13px" color="gray.600">
          <Text flex="1 1 320px">
            La caja inicial no coincide con lo que el sistema calcula para el cierre de {MONTHS[(month + 10) % 12]}. Suele ser un movimiento sin registrar (4x1000, comisiones bancarias) o un error de digitación.
          </Text>
          {canEdit && (
            <>
              <Button variant="link" size="sm" colorScheme="purple" onClick={onNewMovement}>Registrar el movimiento que faltó</Button>
              <Button variant="link" size="sm" colorScheme="purple" onClick={onOpen}>Escribir el motivo</Button>
            </>
          )}
        </Flex>
      )}
      {period.status === 'explicada' && period.reason && (
        <Text mt={3} pt={3} borderTop="1px solid" borderColor="purple.100" fontSize="13px" color="gray.600">Motivo: {period.reason}</Text>
      )}
    </Box>
  );
}

/**
 * Bancos y Flujo de caja: una cuenta y un mes. `book` = 'bancos' | 'caja_menor'.
 * Finanzas registra y edita; el superadmin solo consulta y revisa.
 */
export default function BookPage({ book }) {
  const toast = useToast();
  const { user } = useAuth();
  const role = user?.role?.type;
  const canEdit = EDIT_ROLES.includes(role);
  const canReview = REVIEW_ROLES.includes(role);
  const isCaja = book === 'caja_menor';
  const words = isCaja ? ['Salidas', 'Entradas'] : ['Egresos', 'Ingresos'];

  const [options, setOptions] = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [period, setPeriod] = useState(null);
  const [filters, setFilters] = useState(FILTERS);
  const [page, setPage] = useState(1);
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);

  const [drawer, setDrawer] = useState(null); // { movement?, type? }
  const [openingOpen, setOpeningOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(null); // { name, kind } iniciales
  const [historyId, setHistoryId] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [platformsOpen, setPlatformsOpen] = useState(false);

  const q = useDebounced(filters.q);
  const min = useDebounced(filters.min);
  const max = useDebounced(filters.max);
  const studentCode = useDebounced(filters.studentCode);

  const loadOptions = useCallback(async () => {
    try {
      const data = await getFinanceOptions();
      setOptions(data);
      const [y, m] = data.today.split('-').map(Number);
      setPeriod((p) => p || { year: y, month: m });
      setAccountId((id) => id || data.accounts.find((a) => a.book === book && a.active)?.id || null);
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  }, [book, toast]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const accounts = useMemo(() => (options?.accounts || []).filter((a) => a.book === book && a.active), [options, book]);
  const account = accounts.find((a) => a.id === accountId);
  const cardPlatforms = account && options?.cardPlatforms?.accountCode === account.code ? options.cardPlatforms : null;
  const today = options ? { year: Number(options.today.slice(0, 4)), month: Number(options.today.slice(5, 7)) } : null;

  const load = useCallback(async () => {
    if (!accountId || !period) return;
    setLoading(true);
    try {
      const params = { account: accountId, year: period.year, month: period.month };
      const [s, l] = await Promise.all([
        getSummary(params),
        getMovements({ ...params, q, type: filters.type, category: filters.category, note: filters.note, min, max, studentCode, page, pageSize: PAGE_SIZE }),
      ]);
      setSummary(s);
      setList(l);
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    } finally {
      setLoading(false);
    }
  }, [accountId, period, q, filters.type, filters.category, filters.note, min, max, studentCode, page, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [accountId, period, q, filters.type, filters.category, filters.note, min, max, studentCode]);

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const categoriesOfBook = (options?.categories || []).filter((c) => c.book === book);

  async function exportExcel(scope) {
    try {
      await downloadExport({ account: accountId, year: period.year, month: period.month, scope, q, type: filters.type, category: filters.category, note: filters.note, min, max });
    } catch (e) {
      toast({ status: 'error', title: 'No se pudo exportar el Excel.' });
    }
  }

  const askVoid = (m) => setPrompt({
    title: 'Anular movimiento',
    description: m.isTransfer ? 'Es un traslado: se anulan los dos movimientos (origen y destino).' : 'Deja de contar en el saldo y en las gráficas. Queda en Auditoría.',
    label: 'Motivo',
    confirmLabel: 'Anular',
    danger: true,
    onConfirm: async (reason) => {
      try {
        await voidMovement(m.id, reason);
        toast({ status: 'success', title: 'Movimiento anulado', duration: 2500 });
        load();
      } catch (e) {
        toast({ status: 'error', title: errorMessage(e) });
      }
    },
  });

  const review = async (m, status, comment) => {
    try {
      await reviewMovement(m.id, status, comment);
      load();
    } catch (e) {
      toast({ status: 'error', title: errorMessage(e) });
    }
  };

  const askObserve = (m) => setPrompt({
    title: 'Observar movimiento',
    description: 'Finanzas ve tu observación en el movimiento y en el filtro «Observados».',
    label: 'Qué observas',
    confirmLabel: 'Observar',
    onConfirm: (comment) => review(m, 'observado', comment),
  });

  if (!options) {
    return <Flex justify="center" py={20}><Spinner /></Flex>;
  }

  const title = isCaja ? 'Flujo de caja' : 'Bancos';
  const totals = summary?.totals;
  const monthName = period ? MONTHS[period.month - 1] : '';

  return (
    <Box bg="gray.50" mx="-20px" mt="-20px" p={{ base: 4, md: 8 }} minH="calc(100vh - 80px)">
      <PageHeader
        breadcrumb={[{ label: 'Finanzas', to: '/plataforma/administrador/finanzas' }, { label: title }]}
        title={title}
        subtitle={isCaja ? 'Caja menor en efectivo: entradas, salidas y saldo.' : 'Día a día de las cuentas bancarias y las tarjetas.'}
        actions={
          <>
            {canEdit && (
              <Button variant="outline" bg="white" leftIcon={<FiSliders />} onClick={() => setOpeningOpen(true)}>
                {isCaja ? 'Configurar caja' : 'Caja inicial del mes'}
              </Button>
            )}
            <Button variant="outline" bg="white" leftIcon={<FiTag />} onClick={() => setCategoriesOpen({ name: '' })}>Categorías</Button>
            <Menu>
              <MenuButton as={Button} variant="outline" bg="white" leftIcon={<FiDownload />} rightIcon={<FiChevronDown />}>Exportar a Excel</MenuButton>
              <MenuList>
                <MenuItem onClick={() => exportExcel('account')}>
                  <Box><Text>Esta cuenta</Text><Text fontSize="xs" color="gray.500">{account?.name} · {monthName}, con los filtros aplicados</Text></Box>
                </MenuItem>
                {!isCaja && (
                  <MenuItem onClick={() => exportExcel('all')}>
                    <Box><Text>Todas las cuentas</Text><Text fontSize="xs" color="gray.500">Una hoja por cuenta · {monthName}</Text></Box>
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
            {canEdit && account && (
              <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} leftIcon={<FiPlus />} onClick={() => setDrawer({})}>Nuevo movimiento</Button>
            )}
          </>
        }
      />

      <Flex justify="space-between" gap={3} wrap="wrap" mb={5} align="center">
        {!isCaja ? (
          <Flex gap={3} wrap="wrap" align="center">
            <SegmentedButtons ariaLabel="Cuenta" value={accountId} onChange={setAccountId} options={accounts.map((a) => ({ value: a.id, label: a.currency === 'USD' ? `${a.name} · USD` : a.name }))} />
            {cardPlatforms && (
              <Button size="sm" variant="outline" bg="white" leftIcon={<FiCreditCard />} onClick={() => setPlatformsOpen(true)}>Plataformas de la tarjeta</Button>
            )}
          </Flex>
        ) : <Box />}
        {period && <MonthStepper value={period} onChange={setPeriod} max={today} />}
      </Flex>

      {!account ? (
        <Card><EmptyState title="No hay cuentas activas" description="Las cuentas se agregan desde el panel de Strapi." /></Card>
      ) : !summary ? (
        <Flex justify="center" py={16}><Spinner /></Flex>
      ) : (
        <Flex direction="column" gap={5}>
          {summary.alert === 'sobre_maximo' && (
            <Alert status="error" borderRadius="lg" variant="subtle" alignItems="center" flexWrap="wrap" gap={2}>
              <AlertIcon />
              <Text flex="1 1 360px" fontSize="sm"><b>La caja está por encima del máximo.</b> Tiene {formatMoney(totals.balance)} y el máximo es {formatMoney(account.maxBalance)}. Consigna el excedente y regístralo como traslado.</Text>
              {canEdit && <Button size="sm" variant="outline" bg="white" onClick={() => setDrawer({ type: 'traslado' })}>Registrar consignación</Button>}
            </Alert>
          )}
          {summary.alert === 'bajo_minimo' && (
            <Alert status="warning" borderRadius="lg" variant="subtle">
              <AlertIcon />
              <Text fontSize="sm"><b>La caja está por debajo del mínimo.</b> Tiene {formatMoney(totals.balance)} y el mínimo es {formatMoney(account.minBalance)}.</Text>
            </Alert>
          )}

          <OpeningBanner account={account} period={summary.period} year={period.year} month={period.month} canEdit={canEdit} onOpen={() => setOpeningOpen(true)} onNewMovement={() => setDrawer({})} />

          {summary.period.opening !== null && (
            <>
              <Grid templateColumns={{ base: '1fr 1fr', lg: 'repeat(4, minmax(0, 1fr))' }} gap={4}>
                <KpiTile label={words[1]} value={formatMoney(totals.in, account.currency)} hint={`${totals.countIn} movimientos`} tone="good" />
                <KpiTile label={words[0]} value={formatMoney(totals.out, account.currency)} hint={`${totals.countOut} movimientos`} />
                <KpiTile label="Neto" value={formatMoney(totals.net, account.currency)} hint={`${words[1]} menos ${words[0].toLowerCase()}`} tone={totals.net < 0 ? 'bad' : undefined} />
                <KpiTile
                  label={isCaja ? 'Saldo en caja' : 'Saldo calculado'}
                  value={formatMoney(totals.balance, account.currency)}
                  hint={summary.alert === 'sobre_maximo' ? 'Por encima del máximo' : summary.alert === 'bajo_minimo' ? 'Por debajo del mínimo' : 'Caja inicial + ingresos − egresos'}
                  tone={summary.alert ? 'bad' : undefined}
                />
              </Grid>

              <Card padding={4}>
                <Flex gap={2.5} wrap="wrap" align="center">
                  <InputGroup flex="1 1 280px" size="sm">
                    <InputLeftElement pointerEvents="none"><FiSearch color="#a0aec0" /></InputLeftElement>
                    <Input aria-label="Buscar" placeholder={isCaja ? 'Buscar por detalle o nota' : 'Buscar por concepto, tercero o nota'} value={filters.q} onChange={setFilter('q')} bg="white" borderRadius="md" />
                  </InputGroup>
                  <Select size="sm" w="auto" aria-label="Tipo" value={filters.type} onChange={setFilter('type')} bg="white" borderRadius="md">
                    <option value="">Tipo: todos</option>
                    <option value="ingreso">{words[1]}</option>
                    <option value="egreso">{words[0]}</option>
                    <option value="traslado">Traslados</option>
                  </Select>
                  <Select size="sm" w="auto" aria-label="Categoría" value={filters.category} onChange={setFilter('category')} bg="white" borderRadius="md">
                    <option value="">Categoría: todas</option>
                    <option value="none">Sin categoría</option>
                    {categoriesOfBook.map((c) => <option key={c.id} value={c.id}>{c.name}{c.active ? '' : ' (inactiva)'}</option>)}
                  </Select>
                  <Select size="sm" w="auto" aria-label="Nota y soporte" value={filters.note} onChange={setFilter('note')} bg="white" borderRadius="md">
                    <option value="">Nota y soporte: todos</option>
                    <option value="with_note">Con nota</option>
                    <option value="no_attachment">Sin soporte</option>
                    <option value="observed">Observados</option>
                  </Select>
                  {isCaja && <Input size="sm" w="140px" aria-label="Código de estudiante" placeholder="Código estudiante" value={filters.studentCode} onChange={setFilter('studentCode')} bg="white" borderRadius="md" />}
                  <Input size="sm" w="110px" type="number" aria-label="Monto mínimo" placeholder="$ mínimo" value={filters.min} onChange={setFilter('min')} bg="white" borderRadius="md" />
                  <Input size="sm" w="110px" type="number" aria-label="Monto máximo" placeholder="$ máximo" value={filters.max} onChange={setFilter('max')} bg="white" borderRadius="md" />
                  <Button size="sm" variant="link" colorScheme="purple" ml="auto" onClick={() => setFilters(FILTERS)}>Limpiar filtros</Button>
                </Flex>
              </Card>

              <Grid templateColumns={{ base: '1fr', xl: 'repeat(2, minmax(0, 1fr))' }} gap={4}>
                <CategoryBars data={summary.byCategory} words={words} unit={isCaja ? 'pesos' : 'millions'} subtitle={`${monthLabel(period.year, period.month)}${isCaja ? ', en pesos' : ', en millones'}`} />
                {isCaja
                  ? <DailyBalance points={summary.daily} maxBalance={account.maxBalance} />
                  : <PairBars title={`${words[1]} y ${words[0].toLowerCase()} por semana`} subtitle={`${monthLabel(period.year, period.month)}, en millones`} items={summary.byWeek} />}
              </Grid>
            </>
          )}

          <Card
            padding={0}
            title={<Box as="span" display="block" px={5} pt={5}>Movimientos</Box>}
            description={<Box as="span" px={5} display="block">{list ? `${list.pagination.total} movimientos de ${account.name} en ${monthName}` : ''} · el saldo lo calcula el sistema</Box>}
            actions={summary.uncategorized > 0 && canEdit ? (
              <Box pr={5} pt={5}>
                <Button as={RouterLink} to={`/plataforma/administrador/finanzas/asignar-categorias?libro=${book}`} size="sm" variant="outline" colorScheme="orange" bg="orange.50">
                  {summary.uncategorized.toLocaleString('es-CO')} sin categoría · Asignar en bloque
                </Button>
              </Box>
            ) : null}
          >
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th bg="gray.50">Fecha</Th>
                    {isCaja ? <Th bg="gray.50">Detalle</Th> : <Th bg="gray.50">Concepto</Th>}
                    <Th bg="gray.50">Categoría</Th>
                    {isCaja && <Th bg="gray.50">Código</Th>}
                    <Th bg="gray.50" isNumeric>{words[1].slice(0, -1)}</Th>
                    <Th bg="gray.50" isNumeric>{words[0].slice(0, -1)}</Th>
                    <Th bg="gray.50" isNumeric>Saldo</Th>
                    <Th bg="gray.50">Revisión</Th>
                    <Th bg="gray.50"><VisuallyHidden>Acciones</VisuallyHidden></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {list?.opening?.balance !== null && list?.opening?.balance !== undefined && page === 1 && (
                    <Tr bg="purple.50">
                      <Td color="purple.800">{formatDay(list.opening.date)}</Td>
                      <Td color="purple.800" fontWeight="600" colSpan={isCaja ? 4 : 3}>Caja inicial del mes</Td>
                      <Td />
                      <Td isNumeric color="purple.800" fontWeight="600">{formatAmount(list.opening.balance, account.currency)}</Td>
                      <Td colSpan={2}>{canEdit && <Button size="xs" variant="link" colorScheme="purple" onClick={() => setOpeningOpen(true)}>Editar</Button>}</Td>
                    </Tr>
                  )}
                  {(list?.data || []).map((m) => (
                    <Tr key={m.id}>
                      <Td color="gray.600" whiteSpace="nowrap">{formatDay(m.date)}</Td>
                      <Td whiteSpace="normal" minW="240px">
                        <Text fontWeight="500">{m.concept}</Text>
                        {m.note && <Flex gap={1.5} align="center" fontSize="xs" color="gray.500"><FiMessageSquare color="#805ad5" />{m.note}</Flex>}
                        {m.reviewStatus === 'observado' && m.reviewComment && <Text fontSize="xs" color="orange.700">Observación: {m.reviewComment}</Text>}
                        {m.attachments?.length > 0 && <Flex gap={1} align="center" fontSize="xs" color="gray.500"><FiPaperclip />{m.attachments.length} soporte(s)</Flex>}
                      </Td>
                      <Td>
                        {m.isTransfer ? <Tag bg="blue.100" color="blue.800">Traslado</Tag> : m.category ? <Tag>{m.category.name}</Tag> : <Tag bg="gray.50" color="gray.500">Sin categoría</Tag>}
                      </Td>
                      {isCaja && <Td>{m.studentCode || ''}</Td>}
                      <Td isNumeric color="green.700" fontWeight="600">{m.type === 'ingreso' ? formatAmount(m.amount, account.currency) : ''}</Td>
                      <Td isNumeric>{m.type === 'egreso' ? formatAmount(m.amount, account.currency) : ''}</Td>
                      <Td isNumeric color="gray.600">{formatAmount(m.balance, account.currency)}</Td>
                      <Td>{REVIEW_LABEL[m.reviewStatus] && <Tag bg={m.reviewStatus === 'observado' ? 'orange.100' : 'green.100'} color={m.reviewStatus === 'observado' ? 'orange.800' : 'green.800'}>{REVIEW_LABEL[m.reviewStatus]}</Tag>}</Td>
                      <Td textAlign="right">
                        <Menu placement="bottom-end">
                          <MenuButton as={IconButton} aria-label="Acciones del movimiento" icon={<FiMoreHorizontal />} size="sm" variant="ghost" />
                          <MenuList fontSize="sm">
                            {canEdit && <MenuItem onClick={() => setDrawer({ movement: m })}>Editar</MenuItem>}
                            <MenuItem onClick={() => setHistoryId(m.id)}>Ver historial</MenuItem>
                            {canReview && <MenuItem onClick={() => review(m, 'revisado')}>Marcar revisado</MenuItem>}
                            {canReview && <MenuItem onClick={() => askObserve(m)}>Observar…</MenuItem>}
                            {canReview && m.reviewStatus !== 'ninguno' && <MenuItem onClick={() => review(m, 'ninguno')}>Quitar revisión</MenuItem>}
                            {canEdit && <MenuItem color="red.600" onClick={() => askVoid(m)}>Anular</MenuItem>}
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            {list && list.data.length === 0 && (
              <EmptyState title="No hay movimientos" description={Object.values(filters).some(Boolean) ? 'Prueba con otros filtros.' : `Aún no hay movimientos de ${account.name} en ${monthName}.`} />
            )}
            {loading && <Flex justify="center" py={3}><Spinner size="sm" /></Flex>}
            <AiPagination pagination={list?.pagination} noun="movimientos" onPageChange={setPage} />
          </Card>
        </Flex>
      )}

      {account && (
        <MovementDrawer
          isOpen={!!drawer}
          onClose={() => setDrawer(null)}
          account={account}
          accounts={options.accounts}
          categories={options.categories}
          movement={drawer?.movement}
          initialType={drawer?.type}
          onSaved={load}
          onCreateCategory={(name, kind) => setCategoriesOpen({ name, kind })}
        />
      )}
      {account && period && (
        <OpeningModal
          isOpen={openingOpen}
          onClose={() => setOpeningOpen(false)}
          account={account}
          accounts={options.accounts}
          initialMonth={period}
          canEdit={canEdit}
          onSaved={() => { loadOptions(); load(); }}
        />
      )}
      <CategoriesModal
        isOpen={categoriesOpen !== null}
        onClose={() => setCategoriesOpen(null)}
        book={book}
        categories={options.categories}
        canEdit={canEdit}
        initialName={categoriesOpen?.name}
        initialKind={categoriesOpen?.kind}
        onChanged={loadOptions}
      />
      <HistoryDrawer movementId={historyId} onClose={() => setHistoryId(null)} />
      <PromptModal isOpen={!!prompt} onClose={() => setPrompt(null)} {...(prompt || {})} />
      {cardPlatforms && <CardPlatformsModal isOpen={platformsOpen} onClose={() => setPlatformsOpen(false)} platforms={cardPlatforms} />}
    </Box>
  );
}

